import { supabaseUserMiddleware } from "@/middleware/supabase-user-middleware";
import { createServerFn } from "@tanstack/react-start";
import z from "zod";
import { createClient } from "./supabase/admin";
import { queryOptions } from "@tanstack/react-query";
import { CouponContext } from "./supabase/enums";
import { normalizeCouponCode } from "@vrodex/utils";

export const couponsQueryOptions = () =>
  queryOptions({
    queryKey: ["coupons"],
    queryFn: () => getCoupons(),
  });

export const getCoupons = createServerFn({ method: "GET" })
  .middleware([supabaseUserMiddleware])
  .handler(async ({ context: { userId } }) => {
    const supabase = createClient();
    const { data: coupon, error: couponsError } = await supabase
      .from("coupon")
      .select("code")
      .eq("auto_apply", true)
      .eq("user_id", userId)
      .limit(1)
      .maybeSingle();

    if (couponsError) {
      console.error(couponsError);
      throw new Error("Failed to get coupons");
    }

    return coupon;
  });

export const checkCoupon = createServerFn()
  .middleware([supabaseUserMiddleware])
  .inputValidator(
    z.object({
      /**
       * Accepted in any casing and with stray whitespace — it is normalised
       * before the lookup. A codeless coupon reaches this as null, which simply
       * resolves to "not found" rather than a validation error.
       */
      code: z.string().nullish(),
      /**
       * Where the order is being placed. A QR guest is sitting at a table, so
       * their round is an `in_store` order even though it is taken through the
       * online ordering app — a coupon the merchant limited to the webshop must
       * not come off a table bill. Omitted means "do not narrow", which keeps
       * the ordinary delivery/pickup checkout behaving as before.
       */
      context: z.nativeEnum(CouponContext).optional(),
    })
  )
  .handler(async ({ context: { userId }, data: { code, context } }) => {
    const supabase = createClient();

    // Codes are stored normalised, so the same normalisation has to be applied
    // to whatever the guest typed before comparing the two.
    const normalizedCode = normalizeCouponCode(code);

    if (!normalizedCode) {
      return {
        success: false,
        code: "coupon_not_found",
      };
    }

    const { data: coupon, error: couponError } = await supabase
      .from("coupon")
      .select(
        "id, code, name, type, status, valid_from, valid_until, usage_count, usage_limit"
      )
      .eq("code", normalizedCode)
      .eq("user_id", userId)
      .eq("status", "active")
      .maybeSingle();



    if (couponError) {
      console.error(couponError);
      throw new Error("Failed to check coupon");
    }

    if (!coupon) {
      return {
        success: false,
        code: "coupon_not_found",
      };
    }

    // A coupon that names contexts only counts in those; one with no contexts
    // configured is unrestricted and still applies everywhere.
    if (context) {
      const { data: couponContexts, error: contextsError } = await (supabase
        .from("context_to_coupon" as any)
        .select("context")
        .eq("coupon_id", coupon.id) as any);

      if (contextsError) {
        console.error(contextsError);
        throw new Error("Failed to check coupon contexts");
      }

      const contexts = (couponContexts ?? []).map(
        (row: { context: string }) => row.context
      );

      if (contexts.length > 0 && !contexts.includes(context)) {
        return {
          success: false,
          code: "coupon_not_valid_in_context",
        };
      }
    }

    // Check if coupon is valid (within date range and usage limits)
    const now = new Date();
    const validFrom = new Date(coupon.valid_from);
    const validUntil = coupon.valid_until ? new Date(coupon.valid_until) : null;

    if (now < validFrom) {
      return {
        success: false,
        code: "coupon_not_yet_valid",
      };
    }

    if (validUntil && now > validUntil) {
      return {
        success: false,
        code: "coupon_expired",
      };
    }

    if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
      return {
        success: false,
        code: "coupon_usage_limit_reached",
      };
    }

    // Get cart adjustment details if it's a cart_adjustment coupon
    if (coupon.type === "cart_adjustment") {
      const { data: cartAdjustment, error: adjustmentError } = await supabase
        .from("cart_adjustment_coupon")
        .select("discount_type, discount_value, min_cart_value")
        .eq("coupon_id", coupon.id)
        .maybeSingle();

      if (adjustmentError) {
        console.error(adjustmentError);
        throw new Error("Failed to get coupon adjustment details");
      }

      if (!cartAdjustment) {
        return {
          success: false,
          code: "coupon_configuration_error",
        };
      }

      return {
        success: true,
        coupon: {
          id: coupon.id,
          code: normalizedCode,
          name: coupon.name,
          type: coupon.type,
          discountType: cartAdjustment.discount_type,
          discountValue: cartAdjustment.discount_value,
          minCartValue: cartAdjustment.min_cart_value,
        },
      };
    }

    // Get product adjustment details if it's a product_adjustment coupon
    if (coupon.type === "product_adjustment") {
      const { data: productAdjustment, error: adjustmentError } = await supabase
        .from("product_adjustment_coupon")
        .select("discount_type, discount_value, filter_type, min_cart_value")
        .eq("coupon_id", coupon.id)
        .maybeSingle();

      if (adjustmentError) {
        console.error(adjustmentError);
        throw new Error("Failed to get coupon adjustment details");
      }



      if (!productAdjustment) {
        return {
          success: false,
          code: "coupon_configuration_error",
        };
      }

      // Fetch filter data based on filter_type
      let productIds: string[] | undefined;
      let categoryIds: string[] | undefined;

      if (productAdjustment.filter_type === "selected_products") {
        const { data: couponProducts, error: productsError } = await supabase
          .from("coupon_product")
          .select("product_id")
          .eq("coupon_id", coupon.id);

        if (productsError) {
          console.error(productsError);
          throw new Error("Failed to get coupon product filters");
        }

        productIds = couponProducts?.map((cp) => cp.product_id) ?? [];
      } else if (productAdjustment.filter_type === "products_in_category") {
        const { data: couponCategories, error: categoriesError } = await supabase
          .from("coupon_product_category")
          .select("product_category_id")
          .eq("coupon_id", coupon.id);

        if (categoriesError) {
          console.error(categoriesError);
          throw new Error("Failed to get coupon category filters");
        }

        categoryIds = couponCategories?.map((cc) => cc.product_category_id) ?? [];

      }

      return {
        success: true,
        coupon: {
          id: coupon.id,
          code: normalizedCode,
          name: coupon.name,
          type: coupon.type,
          discountType: productAdjustment.discount_type,
          discountValue: productAdjustment.discount_value,
          filterType: productAdjustment.filter_type,
          productIds,
          categoryIds,
          minCartValue: productAdjustment.min_cart_value,
        },
      };
    }

    // For other coupon types, return basic info
    return {
      success: true,
      coupon: {
        id: coupon.id,
        code: normalizedCode,
        name: coupon.name,
        type: coupon.type,
      },
    };
  });

// const applyCoupon = async (code: string) => {
//   if (!code || code.trim() === "") {
//     return {
//       success: false,
//     };
//   }

//   try {
//     const result = await checkCoupon({ data: { code: code.toUpperCase() } });

//     if (!result.success) {
//       let errorMessage = i18n.t("Checkout.Coupon.invalid");
//       switch (result.code) {
//         case "coupon_not_found":
//           errorMessage = i18n.t("Checkout.Coupon.not_found");
//           break;
//         case "coupon_expired":
//           errorMessage = i18n.t("Checkout.Coupon.expired");
//           break;
//         case "coupon_not_yet_valid":
//           errorMessage = i18n.t("Checkout.Coupon.not_yet_valid");
//           break;
//         case "coupon_usage_limit_reached":
//           errorMessage = i18n.t("Checkout.Coupon.usage_limit_reached");
//           break;
//         case "coupon_configuration_error":
//           errorMessage = i18n.t("Checkout.Coupon.configuration_error");
//           break;
//       }

//       return {
//         success: false,
//         error: errorMessage,
//       };
//     }

//     // Check minimum cart value if applicable
//     if (
//       result.coupon?.minCartValue &&
//       itemsTotal < result.coupon.minCartValue / 100
//     ) {
//       setError(
//         t("Checkout.Coupon.min_cart_value_not_met", {
//           amount: formatNumberToCurrency({
//             number: result.coupon.minCartValue / 100,
//           }),
//         })
//       );
//       onChange(null);
//       return;
//     }

//     setError(null);
//     if (result.coupon) {
//       onChange({ code: code.toUpperCase(), coupon: result.coupon });
//     } else {
//       onChange(null);
//     }
//   } catch (err) {
//     setError(t("Checkout.Coupon.invalid"));
//     onChange(null);
//   } finally {
//     setIsChecking(false);
//   }
// };
